import React, { useEffect, useState } from "react";
import { getPasswordChecks, passwordIsValid } from "../lib/accountCredentials.js";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ViewIcon, ViewOffIcon } from "./icons";
import { cn } from "../lib/utils";

/**
 * Password field with show/hide and a live strength checklist.
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.label]
 * @param {string} props.value
 * @param {function(string): void} props.onChange
 * @param {string} [props.confirmValue]
 * @param {function(string): void} [props.onConfirmChange]
 * @param {boolean} [props.showConfirm]
 * @param {string} [props.autoComplete]
 * @param {string} [props.confirmAutoComplete]
 * @param {string} [props.inputClassName]
 * @param {string} [props.className]
 * @param {function(boolean): void} [props.onValidityChange]
 * @returns {JSX.Element}
 */
export default function CreatePasswordField({
  id = "new-password",
  label = "Password",
  value,
  onChange,
  confirmValue = "",
  onConfirmChange,
  showConfirm = false,
  autoComplete = "new-password",
  confirmAutoComplete = "new-password",
  inputClassName,
  className,
  onValidityChange,
}) {
  const [show, setShow] = useState(false);
  const [showConfirmSecret, setShowConfirmSecret] = useState(false);
  const checks = getPasswordChecks(value);
  const strengthValid = passwordIsValid(value);
  const confirmMismatch =
    showConfirm && Boolean(confirmValue) && confirmValue !== value;
  const confirmMissing = showConfirm && !confirmValue;
  const valid = strengthValid && (!showConfirm || (Boolean(confirmValue) && confirmValue === value));

  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  const confirmId = `${id}-confirm`;
  const started = Boolean(value);

  return (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-2">
        <Label htmlFor={id}>
          {label}
          <span className="ml-1 text-error">*</span>
        </Label>
        <div className="relative">
          <Input
            id={id}
            name="password"
            type={show ? "text" : "password"}
            autoComplete={autoComplete}
            required
            value={value}
            onChange={(event) => onChange(event.target.value)}
            aria-invalid={started && !strengthValid}
            className={cn("pr-11", started && !strengthValid && "border-error", inputClassName)}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShow((prev) => !prev)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
            aria-label={show ? "Hide password" : "Show password"}
            aria-pressed={show}
          >
            {show ? <ViewOffIcon size={18} /> : <ViewIcon size={18} />}
          </button>
        </div>
      </div>

      <ul className="space-y-1" aria-label="Password requirements">
        {checks.map((check) => {
          const unmet = started && !check.passed;
          return (
            <li
              key={check.id}
              className={cn("text-sm", unmet ? "text-error" : "text-text-secondary")}
            >
              {check.passed ? "✓" : "•"} {check.label}
            </li>
          );
        })}
      </ul>

      {showConfirm ? (
        <div className="space-y-2">
          <Label htmlFor={confirmId}>
            Confirm password
            <span className="ml-1 text-error">*</span>
          </Label>
          <div className="relative">
            <Input
              id={confirmId}
              name="confirm_password"
              type={showConfirmSecret ? "text" : "password"}
              autoComplete={confirmAutoComplete}
              required
              value={confirmValue}
              onChange={(event) => onConfirmChange?.(event.target.value)}
              aria-invalid={confirmMismatch}
              className={cn("pr-11", confirmMismatch && "border-error", inputClassName)}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowConfirmSecret((prev) => !prev)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
              aria-label={showConfirmSecret ? "Hide password" : "Show password"}
              aria-pressed={showConfirmSecret}
            >
              {showConfirmSecret ? <ViewOffIcon size={18} /> : <ViewIcon size={18} />}
            </button>
          </div>
          {confirmMismatch ? (
            <p className="text-sm text-error" role="alert">
              Passwords do not match.
            </p>
          ) : null}
          {confirmMissing && started ? (
            <p className="text-sm text-error" role="alert">
              Confirm your password.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
